import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { NoteModel, ReduxEntity } from 'Douleutaras/types';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { normalizeItems } from 'Douleutaras/utilities/redux/normalizeItems';
import { contactsSlice } from 'Douleutaras/redux/data/settings/contactsSlice';

const NOTES_ENDPOINT = 'api/public/note';

const notesAdapter = createEntityAdapter<NoteModel>();

export const getNotes = createAsyncThunk(
  'pro/notes/get',
  async (jobId: string | Array<string>, api) => {

    const response = await makeRequest(
      `${NOTES_ENDPOINT}/`,
      {
        method: 'GET',
        additionalGetParams: {
          job: jobId,
          page_size: '100'
        }
      }
    )(api);

    const normalizedResults = normalizeItems(response.data.results);

    return { ...response.data, normalizedResults };
  }
);

export const addNewNote = createAsyncThunk(
  'pro/notes/addNew',
  async (note: NoteModel, api) => {
    const response = await makeRequest(
      `${NOTES_ENDPOINT}/`,
      {
        method: 'POST',
        body: {
          editing: false,
          saving: true,
          ...note
        }
      }
    )(api);

    return response.data;
  }
);

export const updateNote = createAsyncThunk(
  'pro/notes/update',
  async (note: NoteModel, api) => {
    const { updateNoteSlice } = notesSlice.actions;
    const response = await makeRequest(
      `${NOTES_ENDPOINT}/${note.id}`,
      {
        method: 'PATCH',
        body: {
          editing: false,
          saving: true,
          ...note
        }
      }
    )(api);

    return api.dispatch(updateNoteSlice(response.data));
  }
);

export const deleteNote = createAsyncThunk(
  'pro/notes/delete',
  async (id: number, api) => {
    const response = await makeRequest(
      `${NOTES_ENDPOINT}/${id}`,
      {
        method: 'DELETE'
      }
    )(api);

    return id;
  }
);

export const notesSlice = createSlice({
  name: 'notes',
  initialState: notesAdapter.getInitialState({
    loading: false,
    error: false,
    entities: []
  } as ReduxEntity<NoteModel>),
  reducers: {
    updateNoteSlice: (state, action) => {
      state.entities[action.payload.id] = action.payload;
    }
  },
  extraReducers: builder => {
    builder.addCase(getNotes.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getNotes.fulfilled, (state, action) => {
      notesAdapter.upsertMany(state, action.payload.normalizedResults || {});
      state.loading = false;
    });
    builder.addCase(addNewNote.fulfilled, (state, action) => {
      notesAdapter.addOne(state, action.payload);
    });
    builder.addCase(deleteNote.fulfilled, (state, action) => {
      notesAdapter.removeOne(state, action.payload);
    });
  }
});

export default notesSlice.reducer;
